import React, { useState, useCallback } from "react";
import { DataTable, Menu } from "react-native-paper";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { FlatList, View, StyleSheet, Dimensions } from "react-native";
import BadgeCore from "../../../components/svgs/common/BadgeCore";
import BadgeGeneral from "../../../components/svgs/common/BadgeGeneral";
import { CoursesTabModalState } from "./CoursesTabContainer";
import Pill from "../../../components/common/Pill/Pill";
import BadgeElective from "../../../components/svgs/common/BadgeElective";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import { useAppConfig } from "../../../AppConfigProvider";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { CourseGenerationStatus, useMeQuery, useGetUserPermissionsQuery } from "../../../graphql/generated/graphql";
import { RolePermissions } from "../../../constants/rolesAndPermissions";

export interface CoursesTableProps {
  courses: {
    id: string;
    name?: string | null;
    course_code?: string | null;
    learningStandards?: string | null;
    lengthInWeeks?: number | null;
    objectives?: Array<string> | null;
    last_modified?: string | null;
    generationStatus?: CourseGenerationStatus | null;
    isDraft?: boolean | null;
    isEnrollmentAvailable?: boolean | null;
    teachingFaculty: Array<{
      id: string;
      name: string;
    }>;
    enrolledStudents: Array<{
      id: string;
      name: string;
    }>;
    degreesHasCourse: Array<{
      id: string;
      name?: string;
    }>;
  }[];
  onActionModalOpen: (
    courseId: string,
    courseName: string,
    modalType: CoursesTabModalState,
    isCourseProcessing: boolean
  ) => void;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  courseId: string;
  courseName: string;
  courseCount: number;
  isCourseProcessing: boolean;
  canEditCourse: boolean;
  canEditDegrees: boolean;
};

const CoursesTable = ({ courses, onActionModalOpen }: CoursesTableProps) => {
  const { hasDegrees } = useAppConfig();

  const { data: meData } = useMeQuery();
  const { data: userPermissionsData } = useGetUserPermissionsQuery();

  const isAdmin = userPermissionsData?.getUserPermissions.includes(RolePermissions.analyticsViewSchool);

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const handleViewCourse = (courseId: string) => {
    navigation.navigate("Custom Coursework Modules", {
      courseId: courseId,
    });
  };

  const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
    if (courseCode && courseCode.trim()) {
      return `${courseCode} ${courseName}`;
    }
    return courseName;
  };

  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    courseId: null,
    courseName: null,
    courseCount: 0,
    isCourseProcessing: false,
    canEditCourse: false,
    canEditDegrees: false,
  });

  const handleOpenMenu = useCallback(
    (
      event: React.MouseEvent,
      courseId: string,
      courseName: string,
      courseCount: number,
      courseGenerationStatus: CourseGenerationStatus,
      isTeachingThisCourse: boolean
    ) => {
      const { pageX, pageY } = event.nativeEvent;

      setMenuState((prev) => ({
        ...prev,
        visible: false,
      }));

      setTimeout(() => {
        setMenuState({
          visible: true,
          x: pageX,
          y: pageY,
          courseId,
          courseName,
          courseCount,
          isCourseProcessing: !(
            courseGenerationStatus === null ||
            courseGenerationStatus === CourseGenerationStatus.Completed ||
            courseGenerationStatus === CourseGenerationStatus.Failed ||
            courseGenerationStatus === CourseGenerationStatus.GeneratingImageCompleted ||
            courseGenerationStatus === CourseGenerationStatus.GeneratingImageFailed ||
            courseGenerationStatus === CourseGenerationStatus.GeneratingImage
          ),
          canEditCourse:
            (isAdmin || isTeachingThisCourse) &&
            userPermissionsData?.getUserPermissions.includes(RolePermissions.courseEdit),
          canEditDegrees: userPermissionsData?.getUserPermissions.includes(RolePermissions.degreeEdit),
        });
      }, 100);
    },
    []
  );

  const handleCloseMenu = useCallback(() => {
    setMenuState({
      visible: false,
      courseId: null,
      courseName: null,
      courseCount: 0,
      x: 0,
      y: 0,
      isCourseProcessing: false,
      canEditCourse: false,
      canEditDegrees: false,
    });
  }, []);

  const handleActionModalOpen = (
    modalType: CoursesTabModalState,
    courseId?: string,
    courseName?: string,
    isCourseProcessing?: boolean
  ) => {
    setMenuState((prev) => ({ ...prev, visible: false }));
    onActionModalOpen(
      menuState.courseId ?? courseId,
      menuState.courseName ?? courseName,
      modalType,
      menuState.isCourseProcessing ?? isCourseProcessing
    );
  };

  return (
    <>
      <DataTable>
        <DataTable.Header>
          <DataTable.Title style={styles.nameColumn}>
            <CustomText text="Course Name" size="s" weight="medium" style={{}} />
          </DataTable.Title>
          <DataTable.Title style={styles.courseStatusColumn}>
            <CustomText text="Course Status" size="s" weight="medium" style={{}} />
          </DataTable.Title>
          <DataTable.Title style={styles.facultyColumn}>
            <CustomText text="Assigned Faculty" size="s" weight="medium" style={{}} />
          </DataTable.Title>
          {hasDegrees && (
            <DataTable.Title style={styles.degreeColumn}>
              <CustomText text="Degrees" size="s" weight="medium" style={{}} />
            </DataTable.Title>
          )}
          <DataTable.Title style={styles.enrollmentColumn}>
            <CustomText text="Current Enrollment" size="s" weight="medium" style={{}} />
          </DataTable.Title>
          <DataTable.Title style={styles.actionsColumn}>
            <CustomText text="Actions" size="s" weight="medium" style={{}} />
          </DataTable.Title>
        </DataTable.Header>

        <FlatList
          data={courses}
          renderItem={({ item: course }) => {
            const isTeachingThisCourse = course.teachingFaculty.some((faculty) => faculty.id === meData?.me?.id);
            const displayName = formatCourseNameWithCode(course.name, course.course_code);
            return (
              <DataTable.Row style={styles.tableRow}>
                <DataTable.Cell style={styles.nameColumn}>
                  <View style={styles.nameCell}>
                    <CustomText
                      text={displayName}
                      size="s"
                      style={styles.courseName}
                      useTranslationText={false}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    />
                    <CustomText
                      text={
                        !(
                          course.generationStatus === null ||
                          course.generationStatus === CourseGenerationStatus.Completed ||
                          course.generationStatus === CourseGenerationStatus.Failed ||
                          course.generationStatus === CourseGenerationStatus.GeneratingImageCompleted ||
                          course.generationStatus === CourseGenerationStatus.GeneratingImageFailed ||
                          course.generationStatus === CourseGenerationStatus.GeneratingImage
                        )
                          ? "(Currently Processing)"
                          : ""
                      }
                      size="s"
                      style={styles.generationStatus}
                    />
                  </View>
                </DataTable.Cell>
                <DataTable.Cell style={styles.courseStatusColumn}>
                  <Pill
                    text={course.isDraft ? "Draft" : course.isEnrollmentAvailable ? "Published" : "Inactive"}
                    backgroundColor={course.isDraft ? "#6B7280" : course.isEnrollmentAvailable ? "#3E68FE" : "#6B7280"}
                    textColor={course.isDraft ? "#fff" : course.isEnrollmentAvailable ? "#fff" : "#fff"}
                  />
                </DataTable.Cell>
                <DataTable.Cell style={styles.facultyColumn}>
                  <View style={{ flex: 1, justifyContent: "center", alignSelf: "center" }}>
                    {course.teachingFaculty.length > 0 ? (
                      <CustomText
                        text={course.teachingFaculty.map((faculty) => faculty.name).join(", ")}
                        size="s"
                        useTranslationText={false}
                        style={styles.facultyText}
                      />
                    ) : (
                      <NewCustomActionButton
                        text="Assign Faculty"
                        onPress={(e) =>
                          handleActionModalOpen(CoursesTabModalState.ASSIGN_FACULTY, course.id, displayName)
                        }
                        leftIcon={null}
                      />
                    )}
                  </View>
                </DataTable.Cell>
                {hasDegrees && (
                  <DataTable.Cell style={styles.degreeColumn}>
                    <CustomText
                      text={course.degreesHasCourse.map((degree) => degree.name).join(", ")}
                      size="s"
                      style={styles.facultyText}
                      useTranslationText={false}
                    />
                  </DataTable.Cell>
                )}
                <DataTable.Cell style={styles.enrollmentColumn}>
                  <CustomText
                    text={course.enrolledStudents.length.toLocaleString()}
                    size="s"
                    style={styles.enrollmentText}
                    useTranslationText={false}
                  />
                </DataTable.Cell>
                <DataTable.Cell style={styles.actionsColumn}>
                  <NewCustomActionButton
                    text="Manage"
                    onPress={(e) =>
                      handleOpenMenu(
                        e,
                        course.id,
                        displayName,
                        course.teachingFaculty.length,
                        course.generationStatus,
                        isTeachingThisCourse
                      )
                    }
                    leftIcon={null}
                  />
                </DataTable.Cell>
              </DataTable.Row>
            );
          }}
          keyExtractor={(item) => item.id}
        />
      </DataTable>

      <Menu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        contentStyle={styles.menuItem}
        style={{ marginTop: -10 }}>
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => handleActionModalOpen(CoursesTabModalState.DELETE_COURSE)}
          title="Delete Course"
          titleStyle={{ color: menuState.isCourseProcessing || !menuState.canEditCourse ? "#6B7280" : "#DC2626" }}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => handleActionModalOpen(CoursesTabModalState.EDIT_COURSE)}
          title="Edit Course Information"
          titleStyle={{ color: menuState.isCourseProcessing || !menuState.canEditCourse ? "#6B7280" : "#3E68FE" }}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => {
            handleViewCourse(menuState.courseId);
          }}
          title="Edit Course Content"
          titleStyle={{ color: menuState.isCourseProcessing || !menuState.canEditCourse ? "#6B7280" : "#3E68FE" }}
        />
        <Menu.Item
          disabled={menuState.courseCount === 0}
          onPress={() => handleActionModalOpen(CoursesTabModalState.REMOVE_FACULTY)}
          title="Remove Faculty"
          titleStyle={{ color: menuState.courseCount === 0 ? "#6B7280" : "#3E68FE" }}
        />
        <Menu.Item
          disabled={!menuState.canEditCourse}
          onPress={() => handleActionModalOpen(CoursesTabModalState.COURSE_ROSTER)}
          title="Manage Roster"
          titleStyle={{ color: !menuState.canEditCourse ? "#6B7280" : "#3E68FE" }}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing}
          onPress={() => handleActionModalOpen(CoursesTabModalState.CLONE_COURSE)}
          title="Clone Course"
          titleStyle={{ color: menuState.isCourseProcessing ? "#6B7280" : "#3E68FE" }}
        />
        {hasDegrees && (
          <Menu.Item
            disabled={!menuState.canEditDegrees}
            onPress={() => handleActionModalOpen(CoursesTabModalState.COURSE_DEGREES)}
            title="Manage Degrees"
            titleStyle={{ color: !menuState.canEditDegrees ? "#6B7280" : "#3E68FE" }}
          />
        )}
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  nameColumn: { flex: 5 },
  typeColumn: { flex: 2 },
  courseStatusColumn: { flex: 2 },
  facultyColumn: { flex: 3, flexWrap: "wrap", alignItems: "flex-start" },
  degreeColumn: { flex: 3, paddingHorizontal: 30 },
  enrollmentColumn: { flex: 2 },
  actionsColumn: { flex: 1, justifyContent: "flex-end", flexShrink: 1 },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    position: "relative",
    width: "100%",
    paddingVertical: 16,
  },
  nameCell: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
    overflow: "hidden",
  },
  courseName: {
    color: "#111827",
    fontSize: 14,
    fontWeight: "500",
    flexShrink: 1,
    overflow: "hidden",
  },
  generationStatus: {
    color: "#6B7280",
    fontSize: 8,
    fontStyle: "italic",
    flexShrink: 1,
    flexWrap: "wrap",
    flex: 1,
    display: "flex",
    marginRight: 30,
  },
  facultyText: {
    color: "#374151",
    fontSize: 14,
    flexShrink: 1,
    flexWrap: "wrap",
    flex: 1,
    display: "flex",
  },
  enrollmentText: {
    color: "#6B7280",
    fontSize: 14,
  },
  menuItem: {
    minWidth: 150,
    backgroundColor: "#fff",
  },
});

export default CoursesTable;
